import SocketService, { NAMESPACES } from '../SocketService';

// Singleton instance
let instance = null;

class CallSocketService {
  constructor() {
    if (instance) {
      return instance;
    }

    this.socket = new SocketService(NAMESPACES.DEFAULT);
    this.connectionPromise = null;
    this.currentCallId = null;
    this.cleanupFns = [];
    this.isInitialized = false;
    this.listenersSetup = false;

    this.listeners = {
      onIncomingCall: [],
      onCallInitiated: [],
      onCallAccepted: [],
      onCallRejected: [],
      onCallEnded: [],
      onOffer: [],
      onAnswer: [],
      onIceCandidate: [],
      onDisconnected: [],
      onReconnected: [],
    };
    
    // Auto-initialize on creation for automatic reconnection
    this.initialize().catch(error => {
      console.error('❌ CallSocketService auto-initialization failed:', error);
    });
    
    instance = this;
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.ensureConnected();
      this.isInitialized = true;
      console.log('✅ CallSocketService initialized');
    } catch (error) {
      console.error('❌ Failed to initialize CallSocketService:', error);
      // Reset so we can retry
      this.isInitialized = false;
      this.connectionPromise = null;
      throw error;
    }
  }

  static getInstance() {
    if (!instance) {
      instance = new CallSocketService();
    }
    return instance;
  }

  async ensureConnected() {
    if (!this.connectionPromise) {
      this.connectionPromise = this.socket.connect()
        .then(() => {
          console.log('✅ CallSocketService: Connected');
          this.setupListeners();
        })
        .catch(error => {
          console.error('❌ CallSocketService: Failed to connect:', error);
          this.connectionPromise = null;
          throw error;
        });
    }
    return this.connectionPromise;
  }

  async connect() {
    return this.ensureConnected();
  }

  async safeEmit(event, data) {
    await this.ensureConnected();
    return this.emit(event, data);
  }

  generateID() {
    return Math.random().toString(36).substring(2, 9);
  }

  addListener(listener, callback) {
    const id = this.generateID();
    if (!Array.isArray(this.listeners[listener])) {
      this.listeners[listener] = [];
    }
    this.listeners[listener].push({ id, callback });
    return id;
  }

  removeListener(listener, id) {
    if (!Array.isArray(this.listeners[listener])) return;
    this.listeners[listener] = this.listeners[listener].filter((x) => x.id !== id);
  }

  triggerListener(listener, data) {
    const list = this.listeners?.[listener];
    if (!Array.isArray(list) || list.length === 0) return;
    list.forEach(({ callback }) => {
      try {
        callback(data);
      } catch {
      }
    });
  }

  // Setup socket event listeners
  setupListeners() {
    if (!this.socket?.socket) return;

    console.log('🔧 Setting up event listeners for CallSocketService...');

    // Don't setup if already setup
    if (this.listenersSetup) {
      console.log('ℹ️ CallSocketService listeners already setup, skipping...');
      return;
    }

    // Clear any existing cleanup functions first
    this.cleanupFns.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        // Ignore cleanup errors
      }
    });
    this.cleanupFns = [];

    // Set up incoming call listener
    const cleanupIncoming = this.socket.on('call:incoming', (data) => {
      console.log('🔔 call:incoming received:', data);
      this.triggerListener('onIncomingCall', data);
    });
    this.cleanupFns.push(cleanupIncoming);

    // Set up call initiated listener (for outgoing calls)
    const cleanupInitiated = this.socket.on('call:initiated', (data) => {
      console.log('📞 call:initiated received:', data);
      this.currentCallId = data.callId;
      this.triggerListener('onCallInitiated', data);
    });
    this.cleanupFns.push(cleanupInitiated);

    // Set up call accepted listener
    const cleanupAccepted = this.socket.on('call:accepted', (data) => {
      console.log('✅ call:accepted received:', data);
      this.currentCallId = data.callId;
      this.triggerListener('onCallAccepted', data);
    });
    this.cleanupFns.push(cleanupAccepted);

    // Set up call rejected listener
    const cleanupRejected = this.socket.on('call:rejected', (data) => {
      console.log('❌ call:rejected received:', data);
      this.triggerListener('onCallRejected', data);
    });
    this.cleanupFns.push(cleanupRejected);

    // Set up call ended listener
    const cleanupEnded = this.socket.on('call:ended', (data) => {
      console.log('📴 call:ended received:', data);
      this.triggerListener('onCallEnded', data);
    });
    this.cleanupFns.push(cleanupEnded);

    // Set up WebRTC offer listener
    const cleanupOffer = this.socket.on('call:offer', (data) => {
      console.log('📥 call:offer received:', data);
      this.triggerListener('onOffer', data);
    });
    this.cleanupFns.push(cleanupOffer);

    // Set up WebRTC answer listener
    const cleanupAnswer = this.socket.on('call:answer', (data) => {
      console.log('📥 call:answer received:', data);
      this.triggerListener('onAnswer', data);
    });
    this.cleanupFns.push(cleanupAnswer);

    // Set up ICE candidate listener
    const cleanupIce = this.socket.on('call:ice-candidate', (data) => {
      console.log('🧊 call:ice-candidate received:', data);
      this.triggerListener('onIceCandidate', data);
    });
    this.cleanupFns.push(cleanupIce);

    // Track disconnects so subsequent connect() calls can reconnect.
    const cleanupDisconnect = this.socket.on('disconnect', (reason) => {
      console.log('🔌 Call socket disconnected. Reason:', reason);
      this.connectionPromise = null;
      this.triggerListener('onDisconnected', reason);
    });
    this.cleanupFns.push(cleanupDisconnect);

    const cleanupReconnect = this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 Call socket reconnected after ${attemptNumber} attempts`);
      this.connectionPromise = null;
      this.triggerListener('onReconnected', { attemptNumber });
    });
    this.cleanupFns.push(cleanupReconnect);

    // Add a catch-all listener to see ALL events
    const cleanupAny = this.socket.socket.onAny((event, ...args) => {
      console.log(`[SOCKET ANY] Event received: ${event}`, args);
    });
    this.cleanupFns.push(cleanupAny);

    this.listenersSetup = true;
    console.log('✅ Event listeners setup complete for CallSocketService');
  }

  // Emit event
  emit(event, data) {
    return this.socket.emit(event, data);
  }

  // Initiate a call - returns a Promise that resolves with the callId
  initiateCall(recipientId, conversationId, callType = 'audio') {
    return new Promise((resolve, reject) => {
      let timeoutId;
      let unsubInitiated;
      let unsubRejected;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (typeof unsubInitiated === 'function') unsubInitiated();
        if (typeof unsubRejected === 'function') unsubRejected();
      };

      const onInitiated = (data) => {
        cleanup();
        resolve(data.callId);
      };

      const onRejected = (data) => {
        cleanup();
        reject(new Error(data?.reason || 'Call rejected'));
      };

      unsubInitiated = this.onCallInitiated(onInitiated);
      unsubRejected = this.onCallRejected(onRejected);

      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Call initiation timeout'));
      }, 15000);

      this.safeEmit('call:initiate', {
        recipientId,
        conversationId,
        callType,
      });
    });
  }

  // Accept incoming call
  acceptCall(callId) {
    this.currentCallId = callId;
    this.safeEmit('call:accept', { callId });
  }

  // Reject incoming call
  rejectCall(callId, reason = 'User declined') {
    this.safeEmit('call:reject', { callId, reason });
  }

  // End ongoing call
  endCall(callId) {
    this.safeEmit('call:end', { callId });
    this.currentCallId = null;
  }

  // Send WebRTC offer
  sendOffer(callId, offer) {
    this.safeEmit('call:offer', { callId, offer });
  }

  // Send WebRTC answer
  sendAnswer(callId, answer) {
    this.safeEmit('call:answer', { callId, answer });
  }

  // Send ICE candidate
  sendIceCandidate(callId, candidate) {
    this.safeEmit('call:ice-candidate', { callId, candidate });
  }

  // Listen for call initiated (for outgoing calls)
  onCallInitiated(callback) {
    const id = this.addListener('onCallInitiated', callback);
    return () => this.removeListener('onCallInitiated', id);
  }

  // Listen for incoming call
  onIncomingCall(callback) {
    const id = this.addListener('onIncomingCall', callback);
    return () => this.removeListener('onIncomingCall', id);
  }

  // Listen for call accepted
  onCallAccepted(callback) {
    const id = this.addListener('onCallAccepted', callback);
    return () => this.removeListener('onCallAccepted', id);
  }

  // Listen for call rejected
  onCallRejected(callback) {
    const id = this.addListener('onCallRejected', callback);
    return () => this.removeListener('onCallRejected', id);
  }

  // Listen for call ended
  onCallEnded(callback) {
    const id = this.addListener('onCallEnded', callback);
    return () => this.removeListener('onCallEnded', id);
  }

  // Listen for WebRTC offer
  onOffer(callback) {
    const id = this.addListener('onOffer', callback);
    return () => this.removeListener('onOffer', id);
  }

  // Listen for WebRTC answer
  onAnswer(callback) {
    const id = this.addListener('onAnswer', callback);
    return () => this.removeListener('onAnswer', id);
  }

  // Listen for ICE candidate
  onIceCandidate(callback) {
    const id = this.addListener('onIceCandidate', callback);
    return () => this.removeListener('onIceCandidate', id);
  }

  // Remove call-specific listeners (but keep global incoming call listener)
  removeCallListeners() {
    this.listeners.onCallInitiated = [];
    this.listeners.onCallAccepted = [];
    this.listeners.onCallRejected = [];
    this.listeners.onCallEnded = [];
    this.listeners.onOffer = [];
    this.listeners.onAnswer = [];
    this.listeners.onIceCandidate = [];
    // NOTE: onIncomingCall is NOT cleared - it's global and should persist
  }

  // Remove ALL listeners including global ones (use sparingly)
  removeAllCallListeners() {
    this.listeners.onIncomingCall = [];
    this.removeCallListeners();
    // Clean up socket listeners
    this.cleanupFns.forEach(cleanup => cleanup());
    this.cleanupFns = [];
  }

  // Disconnect
  async disconnect(destroy = false) {
    this.removeAllCallListeners();
    this.connectionPromise = null;
    this.isInitialized = false;
    this.listenersSetup = false;
    if (this.socket) {
      await this.socket.disconnect();
    }
    if (destroy) {
      instance = null;
    }
  }
}

export default CallSocketService.getInstance();
